import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Modal } from "@/components/application/modals/modal-controlled";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { apiCall } from "../../lib/apiClient";

/**
 * Axigon credentials (per zone S0/S1/S2) — masked + test login.
 *
 * UC-001 prerequisite (Phase 11): admin globalAdmin sees 3-row Data Table
 * s status badges (kompletní credentials / chybí; last test login OK / Fail
 * / ne-spuštěno). Test Login modal vyvolá POST endpoint a renderuje výsledek.
 *
 * Accessibility:
 *   - axe-core compliance (WCAG AA), light + dark mode (REQ-UI-021/022 floor)
 *   - touch targets ≥ 48×48 px (REQ-UI-008/014/020)
 *   - aria-live="polite" pro status update po test login
 */

interface AxigonZoneRow {
  zone_id: string;
  code: string;
  billing_group_code: string;
  billing_group_display_name: string;
  client_zone_url: string;
  has_credentials: boolean;
  credentials_kid: string | null;
  credentials_updated_at: string | null;
  last_test_login_at: string | null;
  last_test_login_ok: boolean | null;
  last_test_login_message: string | null;
}

interface AxigonZoneListResponse {
  zones: AxigonZoneRow[];
}

interface TestLoginResponse {
  zone_id: string;
  ok: boolean;
  message: string;
}

interface SaveCredentialsResponse {
  zone_id: string;
  kid: string;
  updated_at: string;
}

export function AxigonCredentialsPage() {
  const [zones, setZones] = useState<AxigonZoneRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeZone, setActiveZone] = useState<AxigonZoneRow | null>(null);
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<TestLoginResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  // Edit credentials modal state
  const [editZone, setEditZone] = useState<AxigonZoneRow | null>(null);
  const [editUsername, setEditUsername] = useState<string>("");
  const [editPassword, setEditPassword] = useState<string>("");
  const [editSaving, setEditSaving] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiCall<AxigonZoneListResponse>("/api/admin/credentials/axigon");
      setZones(response.zones);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení seznamu zón selhalo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchZones();
  }, [fetchZones]);

  const closeTestModal = useCallback(() => {
    setActiveZone(null);
    setTestResult(null);
    setTestError(null);
    setTesting(false);
  }, []);

  const openEditModal = useCallback((row: AxigonZoneRow) => {
    setEditZone(row);
    setEditUsername("");
    setEditPassword("");
    setEditError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditZone(null);
    setEditUsername("");
    setEditPassword("");
    setEditError(null);
    setEditSaving(false);
  }, []);

  const saveCredentials = useCallback(async () => {
    if (!editZone) return;
    if (!editUsername.trim() || !editPassword) {
      setEditError("Username a heslo jsou povinné.");
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await apiCall<SaveCredentialsResponse>(
        `/api/admin/credentials/axigon/${editZone.zone_id}`,
        {
          method: "PUT",
          body: JSON.stringify({ username: editUsername.trim(), password: editPassword }),
        },
      );
      await fetchZones();
      closeEditModal();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Uložení credentials selhalo");
    } finally {
      setEditSaving(false);
    }
  }, [editZone, editUsername, editPassword, fetchZones, closeEditModal]);

  const runTestLogin = useCallback(async () => {
    if (!activeZone) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const result = await apiCall<TestLoginResponse>(
        `/api/admin/credentials/axigon/${activeZone.zone_id}/test-login`,
        { method: "POST" },
      );
      setTestResult(result);
      await fetchZones();
    } catch (e) {
      setTestError(e instanceof Error ? e.message : "Test login selhal");
    } finally {
      setTesting(false);
    }
  }, [activeZone, fetchZones]);

  const columns = useMemo<Column<AxigonZoneRow>[]>(
    () => [
      {
        key: "zone",
        header: "Zóna",
        cell: (row) => (
          <div>
            <strong>{row.code}</strong>
            <div className="text-tertiary">{row.billing_group_display_name}</div>
          </div>
        ),
      },
      {
        key: "url",
        header: "Klientská zóna",
        cell: (row) => row.client_zone_url,
      },
      {
        key: "credentials",
        header: "Credentials",
        cell: (row) => renderCredentialsStatus(row),
      },
      {
        key: "last_test",
        header: "Poslední test",
        cell: (row) => renderLastTestStatus(row),
      },
      {
        key: "actions",
        header: <span className="sr-only">Akce</span>,
        cell: (row) => (
          <div className="flex gap-2">
            <Button
              size="sm"
              color="secondary"
              onPress={() => openEditModal(row)}
              aria-label={`Upravit přihlašovací údaje pro zónu ${row.code}`}
            >
              {row.has_credentials ? "Upravit" : "Nastavit"}
            </Button>
            <Button
              size="sm"
              color="secondary"
              onPress={() => {
                setActiveZone(row);
                setTestResult(null);
                setTestError(null);
              }}
              isDisabled={!row.has_credentials}
              aria-label={`Test přihlášení pro zónu ${row.code}`}
            >
              Test login
            </Button>
          </div>
        ),
      },
    ],
    [openEditModal],
  );

  return (
    <section aria-labelledby="credentials-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="credentials-title" className="text-display-xs font-bold text-primary m-0">
          Axigon credentials
        </h2>
        <p className="text-md text-tertiary m-0">
          Přihlašovací údaje do klientské zóny (S0/S1/S2) — uloženy zašifrované, oddělené per zónu.
        </p>
      </header>

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader title="Zóny" />
        <CardBody>
          <DataTable
            caption="Seznam Axigon zón se stavem credentials a posledního test loginu"
            columns={columns}
            rows={zones}
            rowKey={(row) => row.zone_id}
            loading={loading}
            emptyMessage="Žádné zóny zatím nejsou zaregistrovány."
          />
        </CardBody>
      </Card>

      {activeZone && (
        <Modal
          open
          title={`Test login — zóna ${activeZone.code}`}
          onClose={closeTestModal}
          footer={
            <>
              <Button color="secondary" onPress={closeTestModal}>Zavřít</Button>
              <Button onPress={runTestLogin} isDisabled={testing} color="primary">
                {testing ? "Probíhá…" : "Spustit"}
              </Button>
            </>
          }
        >
          <div aria-live="polite" className="space-y-3">
            <p>
              Spustí se přihlášení do klientské zóny <strong>{activeZone.client_zone_url}</strong>{" "}
              pomocí uložených údajů. Aplikace nikdy do Axigonu nezapisuje, jen čte.
            </p>
            {testResult && (
              <InlineAlert variant={testResult.ok ? "success" : "error"}>
                <strong>{testResult.ok ? "OK" : "Fail"}</strong> — {testResult.message}
              </InlineAlert>
            )}
            {testError && <InlineAlert variant="error">{testError}</InlineAlert>}
          </div>
        </Modal>
      )}

      {editZone && (
        <Modal
          open
          title={`${editZone.has_credentials ? "Upravit" : "Nastavit"} credentials — zóna ${editZone.code}`}
          onClose={closeEditModal}
          footer={
            <>
              <Button color="secondary" onPress={closeEditModal}>Zrušit</Button>
              <Button
                onPress={saveCredentials}
                isDisabled={editSaving}
                color="primary"
              >
                {editSaving ? "Ukládám…" : "Uložit"}
              </Button>
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void saveCredentials();
            }}
            aria-live="polite"
          >
            <p className="text-tertiary">
              Přihlašovací údaje se ukládají zašifrované, oddělené per zónu.
              Server nikdy neukládá heslo v čistém textu.
            </p>
            <div className="flex flex-col gap-3 mt-4">
              <Input
                label="Username"
                type="text"
                value={editUsername}
                onChange={setEditUsername}
                autoComplete="off"
                isRequired
                isDisabled={editSaving}
                aria-label="Axigon username"
              />
              <Input
                label="Heslo"
                type="password"
                value={editPassword}
                onChange={setEditPassword}
                autoComplete="new-password"
                isRequired
                isDisabled={editSaving}
                aria-label="Axigon heslo"
              />
            </div>
            {editError && (
              <div className="mt-4">
                <InlineAlert variant="error">{editError}</InlineAlert>
              </div>
            )}
          </form>
        </Modal>
      )}
    </section>
  );
}

function renderCredentialsStatus(row: AxigonZoneRow) {
  if (!row.has_credentials) {
    return <Badge color="warning">Chybí</Badge>;
  }
  const updated = row.credentials_updated_at ? new Date(row.credentials_updated_at) : null;
  return (
    <div>
      <Badge color="success">Uloženo</Badge>
      {updated && (
        <div className="text-xs text-tertiary">
          Aktualizováno {updated.toLocaleDateString("cs-CZ")}
        </div>
      )}
      {row.credentials_kid && (
        // kid = identifikátor šifrovacího klíče (interní); zobrazujeme zkráceně.
        <div className="text-xs text-tertiary" title="ID klíče">
          klíč {row.credentials_kid.slice(0, 8)}…
        </div>
      )}
    </div>
  );
}

function renderLastTestStatus(row: AxigonZoneRow) {
  if (row.last_test_login_at == null || row.last_test_login_ok == null) {
    return <Badge color="gray">Nespuštěno</Badge>;
  }
  const at = new Date(row.last_test_login_at);
  return (
    <div>
      <Badge color={row.last_test_login_ok ? "success" : "error"}>
        {row.last_test_login_ok ? "OK" : "Fail"}
      </Badge>
      <div className="text-xs text-tertiary">{at.toLocaleString("cs-CZ")}</div>
      {row.last_test_login_message && (
        <div className="text-xs text-tertiary">{row.last_test_login_message}</div>
      )}
    </div>
  );
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/credentials/axigon",
  component: AxigonCredentialsPage,
});
